import { oktaAuth } from "../../auth/services/oktaAuth";
import { log } from "../../shared/services/debug-logger";
import { apiUrl } from "../../shared/services/service";
import type {
  AdminAgentConversation,
  AdminAgentConversationSummary,
  AdminAgentSettings,
} from "../models/admin-agent.models";
import { ADMIN_AGENT_REQUEST_TIMEOUT_MS } from "../utils/admin-agent.constants";
import { getAdminAgentBrowserTokenScope } from "../utils/admin-agent.utils";
import axios from "axios";

const endpoint = `${apiUrl.replace(/\/+$/, "")}/admin/agent/conversations`;
// The shared API client can reuse a cached bearer after sign-out; private assistant history cannot use that fallback.
const api = axios.create();
const requestOptions = (expectedToken: string | undefined) => {
  const token = oktaAuth.getAccessToken();

  if (
    !token ||
    !expectedToken ||
    getAdminAgentBrowserTokenScope(token, true) !== getAdminAgentBrowserTokenScope(expectedToken)
  ) {
    throw new Error("Your sign-in changed. Wait for the current session or sign in again.");
  }

  return { timeout: ADMIN_AGENT_REQUEST_TIMEOUT_MS, headers: { Authorization: `Bearer ${token}` } };
};
const conversationEndpoint = (id: string): string => `${endpoint}/${encodeURIComponent(id)}`;

// Bind each request to the current rendered session; neither model credentials nor the local proxy belong in the browser.
export const listAdminAgentConversations = async (
  accessToken: string | undefined,
  signal?: AbortSignal,
): Promise<AdminAgentConversationSummary[]> => {
  const response = await api.get<{ conversations: AdminAgentConversationSummary[] }>(endpoint, {
    ...requestOptions(accessToken),
    signal,
  });

  return response.data.conversations;
};

export const getAdminAgentConversation = async (
  accessToken: string | undefined,
  id: string,
  signal?: AbortSignal,
): Promise<AdminAgentConversation> => {
  const response = await api.get<AdminAgentConversation>(conversationEndpoint(id), {
    ...requestOptions(accessToken),
    signal,
  });

  return response.data;
};

export const createAdminAgentConversation = async (
  accessToken: string | undefined,
): Promise<AdminAgentConversation> => {
  const response = await api.post<AdminAgentConversation>(endpoint, {}, requestOptions(accessToken));
  log("REPO - createAdminAgentConversation - response", { status: response.status });

  return response.data;
};

export const renameAdminAgentConversation = async (
  accessToken: string | undefined,
  id: string,
  title: string,
): Promise<AdminAgentConversation> => {
  const response = await api.patch<AdminAgentConversation>(
    conversationEndpoint(id),
    { title },
    requestOptions(accessToken),
  );
  log("REPO - renameAdminAgentConversation - response", { status: response.status });

  return response.data;
};

export const deleteAdminAgentConversation = async (accessToken: string | undefined, id: string): Promise<void> => {
  await api.delete(conversationEndpoint(id), requestOptions(accessToken));
  log("REPO - deleteAdminAgentConversation - completed");
};

export const updateAdminAgentSettings = async (
  accessToken: string | undefined,
  id: string,
  settings: AdminAgentSettings,
): Promise<AdminAgentConversation> => {
  const response = await api.patch<AdminAgentConversation>(
    conversationEndpoint(id),
    { settings },
    requestOptions(accessToken),
  );
  log("REPO - updateAdminAgentSettings - response", { status: response.status });

  return response.data;
};

export const sendAdminAgentMessage = async (
  accessToken: string | undefined,
  id: string,
  message: string,
  requestId: string,
): Promise<AdminAgentConversation> => {
  const response = await api.post<AdminAgentConversation>(
    `${conversationEndpoint(id)}/messages`,
    { message, requestId },
    requestOptions(accessToken),
  );
  log("REPO - sendAdminAgentMessage - response", { status: response.status });

  return response.data;
};

export const stopAdminAgentConversation = async (
  accessToken: string | undefined,
  id: string,
): Promise<AdminAgentConversation> => {
  const response = await api.post<AdminAgentConversation>(
    `${conversationEndpoint(id)}/stop`,
    {},
    requestOptions(accessToken),
  );
  log("REPO - stopAdminAgentConversation - response", { status: response.status });

  return response.data;
};

export const resumeAdminAgentConversation = async (
  accessToken: string | undefined,
  id: string,
  requestId: string,
): Promise<AdminAgentConversation> => {
  const response = await api.post<AdminAgentConversation>(
    `${conversationEndpoint(id)}/resume`,
    { requestId },
    requestOptions(accessToken),
  );
  log("REPO - resumeAdminAgentConversation - response", { status: response.status });

  return response.data;
};

export const decideAdminAgentApproval = async (
  accessToken: string | undefined,
  id: string,
  approvalId: string,
  decision: "approve" | "decline",
): Promise<AdminAgentConversation> => {
  const response = await api.post<AdminAgentConversation>(
    `${conversationEndpoint(id)}/approvals/${encodeURIComponent(approvalId)}`,
    { decision },
    requestOptions(accessToken),
  );
  log("REPO - decideAdminAgentApproval - response", { status: response.status });
  return response.data;
};
